import React, { useMemo, useState } from "react";
import { FullScreenDialog } from "../../core/FullScreenDialog";
import Alert from "@mui/material/Alert";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import { useCreateUnlinkedPublisher, useShortlistPublisher } from "@seabrookstudios/pitch2table";
import { useMergedPublishers } from "../../publishers/use-merged-publishers";
import { enqueueSnackbar } from "notistack";
import { sanitisePublisherName } from "./CustomPublisherCard";
import { useGameIdFromRoute } from "../WithGameIdFromRoute";

export const AddCustomPublisherDialog = () => {
  const [publisherName, setPublisherName] = useState("");
  const { data: publishers = [] } = useMergedPublishers();
  const gameId = useGameIdFromRoute();
  const shortlistPublisher = useShortlistPublisher(gameId);

  const matchingPublishers = useMemo(() => {
    if (!publisherName) return [];
    const toMatch = publisherName.toLowerCase();
    return publishers.filter(([publisher]) => publisher.name.toLowerCase().includes(toMatch)).slice(0, 5);
  }, [publisherName, publishers]);

  const onCreateSuccess = async (publisher) => {
    setPublisherName("");
    enqueueSnackbar(`${publisher.name} added to your custom publishers`, { variant: "success" });
    try {
      await shortlistPublisher.mutateAsync({ publisherIds: [publisher.id] });
    } catch {
      enqueueSnackbar(`${publisher.name} was created but could not be shortlisted`, { variant: "error" });
    }
  };

  const create = useCreateUnlinkedPublisher(onCreateSuccess);

  const onAction = async () => {
    const name = sanitisePublisherName(publisherName);
    if (!name) {
      return;
    }

    await create.mutateAsync({ name });
  };

  return (
    <FullScreenDialog
      title="Add Custom Publisher"
      actionText="Add Publisher"
      canSave={!publisherName || create.isPending || shortlistPublisher.isPending}
      onSaveHandler={onAction}
      isBusy={create.isPending || shortlistPublisher.isPending}
      id="add-custom-publisher"
    >
      <TextField
        fullWidth
        variant="outlined"
        label="Publisher Name"
        value={publisherName}
        onChange={(e) => setPublisherName(e.target.value)}
        autoFocus
        autoComplete="off"
        margin="dense"
      />
      {publisherName.length === 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          As you type, we will search for publishers with similar names.
        </Alert>
      )}
      {publisherName.length > 0 && matchingPublishers.length === 0 && (
        <Alert severity="info" sx={{ mt: 1 }}>
          No publishers found with similar names.
        </Alert>
      )}
      {publisherName.length > 0 && matchingPublishers.length > 0 && (
        <>
          <Alert severity="warning" sx={{ mt: 1 }}>
            {`We found ${matchingPublishers.length} publisher${
              matchingPublishers.length !== 1 ? "s" : ""
            } with similar names. Please confirm the publisher you are looking for is not in the list below.`}
          </Alert>
          <List dense>
            {matchingPublishers.map(([publisher]) => (
              <ListItem key={publisher.id}>
                <ListItemText
                  primary={publisher.name}
                  secondary={publisher.alternateNames?.length > 0 ? `AKA ${publisher.alternateNames.join(", ")}` : ""}
                />
              </ListItem>
            ))}
          </List>
        </>
      )}
    </FullScreenDialog>
  );
};
